import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
    Users, EnvelopeOpen, PaperPlaneTilt, TrendUp, Warning, CheckCircle,
    Cursor, ArrowRight, FileText, Lightning,
} from "@phosphor-icons/react";

function StatCard({ icon: Icon, label, value, sublabel, accent = "cyan", testid }) {
    const colors = {
        cyan: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5",
        orange: "text-orange-300 border-orange-500/30 bg-orange-500/5",
        emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
        red: "text-red-300 border-red-500/30 bg-red-500/5",
    }[accent];
    return (
        <div className="tech-card p-5" data-testid={testid}>
            <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colors}`}>
                    <Icon size={20} weight="duotone" />
                </div>
            </div>
            <div className="text-3xl font-heading font-bold text-slate-50 font-mono-alt">{value}</div>
            <div className="text-sm text-slate-400 mt-1">{label}</div>
            {sublabel && <div className="text-[11px] text-slate-500 mt-1 font-mono-alt">{sublabel}</div>}
        </div>
    );
}

export default function AdminDashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        document.title = "Surveys2CAD | Admin Overview";
        api.get("/admin/analytics/overview").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) {
        return <div className="p-10 text-slate-400">Loading dashboard…</div>;
    }

    const max = Math.max(...data.series_30d.map((d) => Math.max(d.opened || 0, d.clicked || 0, d.sent || 0)), 1);

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6 lg:mb-8">
                <div className="min-w-0">
                    <div className="overline mb-2">Overview</div>
                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight">Campaign Control Center</h1>
                    <p className="text-slate-400 mt-2 text-sm">All leads, campaigns, and engagement at a glance.</p>
                </div>
                <div className="flex gap-2">
                    <Link to="/admin/campaigns/new" className="btn-primary" data-testid="new-campaign-btn">
                        New Campaign <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
            </div>

            {/* Row 1 — Counts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <StatCard icon={PaperPlaneTilt} label="Website leads" value={data.leads_total} accent="orange" testid="stat-leads" />
                <StatCard icon={Users} label="Total contacts" value={data.contacts.total} sublabel={`${data.contacts.active} active`} testid="stat-contacts" />
                <StatCard icon={EnvelopeOpen} label="Total campaigns" value={data.campaigns.total} sublabel={`${data.campaigns.sent} sent · ${data.campaigns.draft} draft`} testid="stat-campaigns" />
                <StatCard icon={TrendUp} label="Overall open rate" value={`${data.emails.open_rate}%`} accent="emerald" sublabel={`${data.emails.click_rate}% clicks`} testid="stat-openrate" />
            </div>

            {/* Row 2 — Email stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <StatCard icon={PaperPlaneTilt} label="Sent" value={data.emails.sent || 0} />
                <StatCard icon={CheckCircle} label="Delivered" value={(data.emails.sent || 0) - (data.emails.bounced || 0) - (data.emails.failed || 0)} accent="emerald" />
                <StatCard icon={EnvelopeOpen} label="Opened" value={data.emails.opened || 0} accent="cyan" />
                <StatCard icon={Cursor} label="Clicked" value={data.emails.clicked || 0} accent="orange" />
                <StatCard icon={Warning} label="Bounced" value={data.emails.bounced || 0} accent="red" />
                <StatCard icon={Warning} label="Failed" value={data.emails.failed || 0} accent="red" />
            </div>

            {/* Time series chart */}
            <div className="tech-card p-4 sm:p-6 mb-6">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div className="min-w-0">
                        <div className="overline mb-1">Last 30 days</div>
                        <h2 className="font-heading text-lg sm:text-xl font-semibold text-slate-50">Engagement timeline</h2>
                    </div>
                    <div className="flex gap-3 sm:gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-cyan-400" /> Opens</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400" /> Clicks</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-slate-500" /> Sends</span>
                    </div>
                </div>
                {data.series_30d.length === 0 ? (
                    <div className="text-slate-500 text-sm text-center py-12">No engagement events yet. Send your first campaign to see data here.</div>
                ) : (
                    <div className="flex items-end gap-1 h-44">
                        {data.series_30d.map((d) => (
                            <div key={d.day} className="flex-1 flex flex-col items-center gap-0.5 group relative" title={d.day}>
                                <div className="w-full flex flex-col gap-[1px] h-40 justify-end">
                                    <div className="bg-slate-600/60 rounded-sm" style={{ height: `${(d.sent / max) * 100}%` }} />
                                    <div className="bg-cyan-400 rounded-sm" style={{ height: `${(d.opened / max) * 100}%` }} />
                                    <div className="bg-orange-400 rounded-sm" style={{ height: `${(d.clicked / max) * 100}%` }} />
                                </div>
                                <span className="text-[9px] text-slate-500 font-mono-alt">{d.day.slice(5)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Quick links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link to="/admin/templates" className="tech-card p-5 group">
                    <FileText size={26} weight="duotone" className="text-cyan-300 mb-3" />
                    <h3 className="font-heading text-lg font-semibold text-slate-50 group-hover:text-cyan-300">Templates Library</h3>
                    <p className="text-sm text-slate-400 mt-1">12 service templates + 6 generic ready to use.</p>
                </Link>
                <Link to="/admin/contacts" className="tech-card p-5 group">
                    <Users size={26} weight="duotone" className="text-cyan-300 mb-3" />
                    <h3 className="font-heading text-lg font-semibold text-slate-50 group-hover:text-cyan-300">Manage Contacts</h3>
                    <p className="text-sm text-slate-400 mt-1">Add, import, export, and tag your contact lists.</p>
                </Link>
                <Link to="/admin/activity" className="tech-card p-5 group">
                    <Lightning size={26} weight="duotone" className="text-cyan-300 mb-3" />
                    <h3 className="font-heading text-lg font-semibold text-slate-50 group-hover:text-cyan-300">Activity Feed</h3>
                    <p className="text-sm text-slate-400 mt-1">Real-time opens, clicks, bounces, and unsubscribes.</p>
                </Link>
            </div>
        </div>
    );
}
